import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactPrimaryButton from 'shared/design-system/components/TrueImpactPrimaryButton';
import { useClient } from 'shared/apollo';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { submitReport, updateReportLevel1DonorName } from './api';
import { hasDonorName } from 'shared/utils/donorNameMatch';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { DONOR_NAME_FLAG_VALUE } from '../../../ReportsBeta/LevelOneFeedback/donorNameFlagUtils';
import { FeedbackType } from '../../../NPOFeedback/SubmitReady/utils';
import { Answer, getReportNextStage, getReportNextStatus } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReportRender, {
  mapReportBuilderAnswersToFeedbackAnswer,
} from './ReportRender';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import AllocateReport from 'shared/components/WelcomeBack/AllocateReport';
import { Report } from 'shared/components/WelcomeBack/utils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { isBbUserView } from 'shared/utils/partnerUser';

interface ReportCompletedProps {
  report: Report;
  reportId: number | string;
  answers: Answer[];
  programName: string;
  feedbackType: FeedbackType;
  isFinalizeStage: boolean;
  reloadReport: () => unknown;
  reloadAnswers: () => unknown;
  handleShowSurvey?: () => unknown;
}

const StyledDiv = styled.div`
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  .icon {
    width: 40px;
    height: 40px;
    margin-bottom: ${SPACERS.small};
    path {
      fill: ${COLORS.success};
    }
  }
  .subheading {
    margin-bottom: ${SPACERS.small};
  }
  .note {
    margin-bottom: ${SPACERS.extraLarge};
  }
  .submit-button {
    margin-left: auto;
    margin-right: auto;
  }
  .edit-survey-button {
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
  }
`;

const ReportCompleted: React.FC<ReportCompletedProps> = ({
  report,
  answers,
  reportId,
  programName,
  feedbackType,
  isFinalizeStage,
  reloadAnswers,
  reloadReport,
  handleShowSurvey,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { isCNUser, userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  const [levelOneFeedbackEnabled, cnReportFeedbackEnabled] = useFeatureFlags(
    'levelOneFeedback',
    'cnReportFeedback',
  );
  const isBbPartner = isBbUserView();
  const isThirdParty = report.cnOptedIn || isBbPartner;
  const [requestState, makeRequest] = useGraphQLRequest();

  const nextStatus = getReportNextStatus(
    report.stage,
    report.status,
    feedbackType,
    isCNUser,
    isThirdParty,
    cnReportFeedbackEnabled,
    isReportEndDatePassed,
    report.totalDonations || 0,
    report.reportingType || 'FULL',
  );
  const nextStage = getReportNextStage(report.stage, isReportEndDatePassed);

  const onClick = useCallback(() => {
    const donorNameDetected =
      levelOneFeedbackEnabled &&
      answers.some(answer => hasDonorName(answer.text, report.sponsorName));
    if (donorNameDetected && client) {
      updateReportLevel1DonorName(
        client,
        report.dbId,
        report.level1DonorName || DONOR_NAME_FLAG_VALUE,
      );
    }
    const request = submitReport(
      client!,
      reportId,
      nextStatus,
      nextStage,
      report.optedInFullReporting || false,
      report.optedInFullReporting ? 'FULL' : undefined,
    );
    request.then(() => {
      const reportData =
        reportState.type === 'REQUEST_SUCCESS' ? reportState.data : undefined;
      trackEvent('ReportStatusChanged', {
        ...(reportData
          ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
          : {}),
        previousStatus: report.status,
        previousStage: report.stage,
        status: nextStatus,
        stage: nextStage ?? report.stage,
      });
    });
    makeRequest(request);
  }, [
    reportId,
    isFinalizeStage,
    feedbackType,
    nextStatus,
    nextStage,
    answers,
    client,
    levelOneFeedbackEnabled,
    report.dbId,
    report.level1DonorName,
    report.sponsorName,
  ]);

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    reloadReport();
    reloadAnswers();
  }, [requestState.type, reloadAnswers, reloadReport]);

  return (
    <StyledDiv>
      {(!report.totalDonations || report.totalDonations === 0) && (
        <AllocateReport report={report} />
      )}
      <FontAwesomeIcon icon={faCheck} className="icon" />
      <TrueImpactText variant="heading-subheading" className="subheading">
        Your report looks great!
      </TrueImpactText>
      <TrueImpactText className="note">
        <strong>Next up:</strong> True Impact will take a closer look at your
        report and reach out to you with any changes needed to make your report
        even better. We’ll send you an email once their feedback is ready for
        your review. Thanks for taking the time to complete this!
      </TrueImpactText>
      <TrueImpactPrimaryButton
        onClick={onClick}
        buttonClassName="submit-button"
        isLoading={
          requestState.type === 'REQUEST_START' ||
          requestState.type === 'REQUEST_SUCCESS'
        }
      >
        {isFinalizeStage || nextStatus === 'Finalized'
          ? 'Lock & Finalize Report'
          : nextStatus === 'Published'
          ? report.stage === 'INTERIM'
            ? 'Publish Interim Updates'
            : 'Publish your report'
          : 'Submit report for feedback'}
      </TrueImpactPrimaryButton>
      {handleShowSurvey && (
        <TrueImpactButton
          variant="secondary-large"
          onClick={handleShowSurvey}
          className="edit-survey-button"
        >
          Edit survey answers
        </TrueImpactButton>
      )}
      <ReportRender
        reportId={reportId}
        programName={programName}
        answers={mapReportBuilderAnswersToFeedbackAnswer(answers)}
      />
    </StyledDiv>
  );
};

export default ReportCompleted;
